import React, { CSSProperties, forwardRef, useContext, useMemo } from 'react';
import { classnames } from '@src/utils/classnames';
import { IAlignItemsProps, IGapProps, IJustifyContentProps, IMarginProps, IPaddingProps } from '@src/models/adaptive';
import { getClasses } from '@src/utils/getClass';
import { useIntersection } from '@src/hooks/useIntersection';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { IDefaultComponentProps } from '@src/models/props';
import { IClickHandler, IVisibleHandler } from '@src/models/handlers';
import { IIdentify } from '@src/models/analytics';
import { TColor } from '@src/models/common';
import styled from './Box.module.scss';
import { getColorClass } from '@src/utils/colorUtils';
import { TColorNew } from '@src/models/colors';

export interface IBoxProps
  extends IDefaultComponentProps,
    IMarginProps,
    IPaddingProps,
    IAlignItemsProps,
    IJustifyContentProps,
    IGapProps,
    IVisibleHandler,
    IIdentify,
    IClickHandler<HTMLDivElement>,
    React.HTMLAttributes<HTMLDivElement> {
  /** Направление базовой линии */
  column?: boolean;

  fullWidth?: boolean;

  /** @deprecated не используй это свойства, из-за него много проблем, придумай как можно решить по-другому */
  noOverflow?: boolean;

  /** Свойство для добавление переносов элементов 'flex-wrap: wrap' */
  wrap?: boolean;

  /** Цвет фона */
  bgColor?: TColorNew;

  /** Рисунок для фона, будет более приоритетным чем bgColor */
  bgImage?: string;

  /** Цвет рамки, рамка рисуется 1px solid */
  borderColor?: TColor;

  isLayout?: boolean;
}

/** Div контейнер для использования в flex верстке */
export const Box = forwardRef<HTMLDivElement, IBoxProps>(
  (
    {
      className,
      children,
      column,
      alignItems = '',
      justifyContent = '',
      marginBottom = '',
      margin = '',
      marginLeft = '',
      marginRight = '',
      marginTop = '',
      padding = '',
      paddingBottom = '',
      paddingLeft = '',
      paddingRight = '',
      paddingTop = '',
      gap = '',
      bgColor = '',
      borderColor = '',
      wrap,
      fullWidth,
      noOverflow,
      onVisible,
      style: propsStyle,
      bgImage,
      isLayout,
      ...rest
    },
    ref,
  ) => {
    const { breakpoint, isMobile } = useContext(BreakpointsContext);

    /** @link https://nextjs.org/docs/messages/react-hydration-error */
    const classes = classnames(
      className,
      styled.root,
      ...getClasses(
        styled,
        {
          alignItems,
          justifyContent,
          margin,
          marginBottom,
          marginTop,
          marginLeft,
          marginRight,
          padding,
          paddingTop,
          paddingBottom,
          paddingLeft,
          paddingRight,
          gap,
        },
        breakpoint,
      ),
      {
        [styled.flexDirection_column]: column,
        [styled.wrap]: wrap,
        [styled.fullWidth]: fullWidth,
        [styled.noOverflow]: noOverflow,
        [styled.border]: !!borderColor,
        [styled.layout]: isLayout,
        [styled.layoutDesktop]: isLayout && !isMobile,
      },
      getColorClass({ view: bgColor, property: 'background' }),
      getColorClass({ view: borderColor, property: 'borderColor' }),
    );

    useIntersection(ref as React.RefObject<HTMLDivElement>, onVisible);

    const style = useMemo(() => {
      let newStyle: CSSProperties | undefined = propsStyle;
      if (bgImage) {
        if (!newStyle) {
          newStyle = {};
        }

        newStyle.backgroundImage = `url(${bgImage})`;
        newStyle.backgroundSize = 'cover';
      }

      return newStyle;
    }, [bgImage, propsStyle]);

    return (
      <div ref={ref} className={classes} style={style} {...rest}>
        {children}
      </div>
    );
  },
);

Box.displayName = 'Box';
